import { css, CSSResult, html, PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { Messages } from '../../../@types/wishlist.js';
import { Drawer } from '@breuni-ui/drawer';
import { safeDefine } from '@breuni-ui/safe-define';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import arrowBackIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_arrow-left.svg?element';
import { config } from '../../../scripts/service/config.js';
import styles from './base-layer.scss?inline';
import { mediaQuery } from '../../../scripts/service/media-query.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';

@customElement('ents-base-layer')
export class BaseLayer extends Drawer {
  @property({ type: Boolean, reflect: true }) private showLayer: boolean = false;

  @property({ type: String, attribute: 'drawer-test-id', reflect: true }) drawerTestId: string = '';

  @consume({ context: i18nContext })
  @property({ attribute: false })
  protected messages!: Messages;

  @consume({ context: wishlistAppConfigContext })
  @property({ attribute: false })
  private wishlistAppConfig!: WishlistAppConfig;

  @query('dialog') private dialogElement!: HTMLDialogElement;
  @query('.ents-bear') private bearElement!: HTMLImageElement;

  private readonly htmlBodyNoScrollClass =
    'ontouchstart' in window || navigator.maxTouchPoints > 0 ? 'ents-html-no-scroll' : 'ents-html-no-scroll__desktop';
  private readonly bearFiguresMobile = ['hands_up', 'points_left', 'points_right'];
  private readonly bearFiguresDesktop = ['hands_up', 'points_left', 'points_right', 'rotated_points_left'];

  static get styles(): CSSResult[] {
    return [
      Drawer.styles,
      css`
        ${unsafeCSS(styles)}
      `,
    ];
  }

  constructor() {
    super();
    this.handleEsc = this.handleEsc.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.wishlistAppConfig.showChristmasBear) {
      this.classList.add('ents-has-bear');
    }
  }

  render() {
    this.closeAriaLabel = this.messages['ents_wishlist_actionmenu_close'] ?? 'Close';

    if (!this.wishlistAppConfig.showChristmasBear) {
      return super.render();
    }

    const bearFigure = mediaQuery.widths.up_to_M
      ? this.bearFiguresMobile[Math.floor(Math.random() * this.bearFiguresMobile.length)] // NOSONAR
      : this.bearFiguresDesktop[Math.floor(Math.random() * this.bearFiguresDesktop.length)]; // NOSONAR
    const bearImageFile = bearFigure === 'rotated_points_left' ? 'points_left' : bearFigure;

    return html`
      ${super.render()}
      <img src="${config.assetsHost}images/bear_${bearImageFile}.svg" alt="" class="ents-bear ${bearFigure}" />
    `;
  }

  private handleEsc(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.triggerClose();
    }
  }

  private triggerClose() {
    this.showLayer = false;
    this.closeDrawer();
  }

  private registerCloseListener() {
    document.addEventListener('keydown', this.handleEsc);
    this.addEventListener(`${this.drawerTestId !== '' ? this.drawerTestId + '-' : ''}drawer:close`, this.triggerClose);
  }

  private unregisterCloseListener() {
    document.removeEventListener('keydown', this.handleEsc);
    this.removeEventListener(
      `${this.drawerTestId === '' ? '' : this.drawerTestId + '-'}drawer:close`,
      this.triggerClose,
    );
  }

  private reveal() {
    this.registerCloseListener();
    this.togglePageScrolling(false);
    this.id = this.drawerTestId;
    super.show();
    this.dispatchEvent(new Event('ents-layer-opened', { composed: true, bubbles: true }));
  }

  private closeDrawer() {
    this.unregisterCloseListener();
    this.togglePageScrolling(true);
    super.hide().then(() => {
      this.dispatchEvent(new Event('ents-layer-closed', { composed: true, bubbles: true }));
    });
  }

  private togglePageScrolling(enabled: boolean) {
    const shopHtml = document.querySelector('.shop-html') as HTMLElement;
    if (!shopHtml) return;
    if (enabled && shopHtml.classList.contains(this.htmlBodyNoScrollClass)) {
      shopHtml.classList.remove(this.htmlBodyNoScrollClass);
    } else if (!enabled && !shopHtml.classList.contains(this.htmlBodyNoScrollClass)) {
      shopHtml.classList.add(this.htmlBodyNoScrollClass);
    }
  }

  public updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('showLayer')) {
      const oldValue = changedProperties.get('showLayer');
      if (this.showLayer && oldValue !== true) {
        this.reveal();
      } else if (!this.showLayer && oldValue === true) {
        this.closeDrawer();
      }
    }

    if (this.dialogElement && this.bearElement) {
      if (this.bearElement.parentElement !== this.dialogElement) {
        this.dialogElement.prepend(this.bearElement);
      }
    }
  }
}

safeDefine('ents-base-layer', BaseLayer);

export const getBackButton = (messages: Messages, testId: string, filterBackCallback: () => void) => {
  return html`
    <button
      slot="header-start"
      class="ents-base-layer__back bdk-link-secondary"
      size="medium"
      aria-label="${messages['ents_wishlist_filter_back']!}"
      data-test-ents-base-layer-back-icon="${testId}"
      @click="${filterBackCallback}"
    >
      ${hideIconFromAssistiveTechnology(arrowBackIcon())}${messages['ents_wishlist_filter_back']!}
    </button>
  `;
};
